import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { formatCurrency } from '../../../utils/currency';

const InvoiceMobileCard = ({ 
  invoice, 
  isSelected, 
  onSelect,
  onView,
  onEdit,
  onDuplicate,
  onSend,
  onApprove,
  onDelete
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatAmount = (amount) => {
    return formatCurrency(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysOverdue = (dueDate, status) => {
    if (status !== 'Overdue') return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysOverdue = getDaysOverdue(invoice?.dueDate, invoice?.status);

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-4 mb-3">
      <div className="flex items-start gap-3">
        <Checkbox
          checked={isSelected}
          onChange={() => onSelect(invoice?.id)}
          className="mt-1"
        />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="data-text text-primary font-semibold">{invoice?.invoiceNumber}</span>
                <InvoiceStatusBadge status={invoice?.status} />
              </div>
              {invoice?.documentType !== 'Invoice' && (
                <p className="caption text-muted-foreground mb-1">{invoice?.documentType}</p>
              )}
              <p className="font-body font-medium text-sm text-foreground truncate">{invoice?.customerName || invoice?.customer}</p>
              <p className="caption text-muted-foreground truncate">{invoice?.customerEmail}</p>
            </div>
            
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-muted rounded transition-smooth flex-shrink-0"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} />
            </button>
          </div>

          <div className="flex items-center justify-between mb-2">
            <span className="data-text font-bold text-lg text-foreground">{formatAmount(invoice?.amount)}</span>
            {invoice?.paidAmount > 0 && invoice?.status === 'Partial' && (
              <span className="caption text-success font-medium">
                {formatAmount(invoice?.paidAmount)} paid
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 caption text-muted-foreground">
            <div>
              <span className="block">Issue Date</span>
              <span className="font-medium text-foreground">{formatDate(invoice?.issueDate)}</span>
            </div>
            <div>
              <span className="block">{invoice?.documentType === 'Quote' ? 'Valid Until' : 'Due Date'}</span>
              <span className="font-medium text-foreground">{formatDate(invoice?.dueDate)}</span>
              {daysOverdue && (
                <span className="block text-error font-medium mt-0.5">
                  {daysOverdue} {daysOverdue === 1 ? 'day' : 'days'} overdue
                </span>
              )}
            </div>
          </div>

          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-border space-y-2">
              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={() => onView(invoice)}
                iconName="Eye"
                iconPosition="left"
              >
                View Invoice
              </Button>
              
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(invoice)}
                  iconName="Edit"
                  iconPosition="left"
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDuplicate(invoice)}
                  iconName="Copy"
                  iconPosition="left"
                >
                  Duplicate
                </Button>
              </div>

              {invoice?.status !== 'Paid' && (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    fullWidth
                    onClick={() => onSend(invoice)}
                    iconName="Send"
                    iconPosition="left"
                  >
                    Send Invoice
                  </Button>
                  
                  {invoice?.status === 'Draft' && invoice?.documentType === 'Invoice' && (
                    <Button
                      variant="success"
                      size="sm"
                      fullWidth
                      onClick={() => onApprove(invoice)}
                      iconName="CheckCircle"
                      iconPosition="left"
                    >
                      Approve Invoice
                    </Button>
                  )}
                </>
              )}

              <Button
                variant="destructive"
                size="sm"
                fullWidth
                onClick={() => onDelete(invoice)}
                iconName="Trash2"
                iconPosition="left"
              >
                Delete Invoice
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceMobileCard;